import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  Input,
  List,
  ListItem,
  ListItemButton,
  ListItemContent,
  Modal,
  ModalDialog,
  Stack,
  Typography,
} from "@mui/joy";
import SearchIcon from "@mui/icons-material/Search";
import CloseIcon from "@mui/icons-material/Close";

import { WorkspaceUser } from "../../../common/types";
import { ConversationCollaborator } from "../services/collaboration";
import {
  getConversationOwner,
  workspaceMembersForTypeahead,
} from "../utils/collaboration";

interface CollaboratorManagementModalProps {
  open: boolean;
  workspaceUsers: WorkspaceUser[];
  collaborators: ConversationCollaborator[];
  loading?: boolean;
  saving?: boolean;
  error?: string | null;
  onClose: () => void;
  onAdd: (userIds: string[]) => Promise<void>;
  onRemove: (userId: string) => Promise<void>;
}

export const CollaboratorManagementModal: React.FC<
  CollaboratorManagementModalProps
> = ({
  open,
  workspaceUsers,
  collaborators,
  loading = false,
  saving = false,
  error = null,
  onClose,
  onAdd,
  onRemove,
}) => {
  const [query, setQuery] = useState("");
  const [selectedUsers, setSelectedUsers] = useState<WorkspaceUser[]>([]);
  const [actionError, setActionError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) {
      setQuery("");
      setSelectedUsers([]);
      setActionError(null);
    }
  }, [open]);

  const owner = useMemo(
    () => getConversationOwner(collaborators),
    [collaborators],
  );

  const selectableMembers = useMemo(
    () =>
      workspaceMembersForTypeahead({
        workspaceUsers,
        currentCollaborators: collaborators,
        ownerUserId: owner?.user_id,
        query,
      }).filter(
        (member) =>
          !selectedUsers.some(
            (selected) => selected.user_id === member.user_id,
          ),
      ),
    [workspaceUsers, collaborators, owner?.user_id, query, selectedUsers],
  );

  const handleSelectUser = (user: WorkspaceUser) => {
    setActionError(null);
    setSelectedUsers((current) =>
      current.some((entry) => entry.user_id === user.user_id)
        ? current
        : [...current, user],
    );
    setQuery("");
  };

  const handleRemoveSelected = (userId: string) => {
    setActionError(null);
    setSelectedUsers((current) =>
      current.filter((entry) => entry.user_id !== userId),
    );
  };

  const handleAdd = async () => {
    if (selectedUsers.length === 0 || saving) return;
    setActionError(null);
    try {
      await onAdd(selectedUsers.map((user) => user.user_id));
      setSelectedUsers([]);
      setQuery("");
    } catch (err: any) {
      setActionError(err?.message || "Failed to add collaborators.");
    }
  };

  const handleRemoveCurrent = async (userId: string) => {
    setActionError(null);
    try {
      await onRemove(userId);
    } catch (err: any) {
      setActionError(err?.message || "Failed to remove collaborator.");
    }
  };

  return (
    <Modal open={open} onClose={onClose}>
      <ModalDialog sx={{ width: "min(100%, 640px)", maxHeight: "85vh" }}>
        <DialogTitle>Add collaborators to this chat</DialogTitle>
        <DialogContent sx={{ overflow: "auto" }}>
          <Stack spacing={2}>
            {(error || actionError) && (
              <Alert color="danger" variant="soft">
                {actionError || error}
              </Alert>
            )}

            <Box>
              <Typography level="body-sm" sx={{ mb: 0.75 }}>
                Workspace members
              </Typography>
              <Input
                value={query}
                onChange={(event) => setQuery(event.target.value)}
                startDecorator={<SearchIcon />}
                placeholder="Search workspace members"
                data-testid="collaborator-search-input"
              />
              <Typography level="body-xs" color="neutral" sx={{ mt: 0.75 }}>
                Pick from workspace members only. You cannot add users outside
                this workspace.
              </Typography>
            </Box>

            {selectedUsers.length > 0 && (
              <Stack spacing={0.75}>
                <Typography level="body-sm">Selected</Typography>
                <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                  {selectedUsers.map((user) => (
                    <Chip
                      key={user.user_id}
                      size="sm"
                      variant="soft"
                      endDecorator={
                        <IconButton
                          size="sm"
                          variant="plain"
                          onClick={() => handleRemoveSelected(user.user_id)}
                          aria-label={`Remove ${user.email}`}
                        >
                          <CloseIcon />
                        </IconButton>
                      }
                    >
                      {user.email}
                    </Chip>
                  ))}
                </Stack>
              </Stack>
            )}

            <Box>
              <Typography level="body-sm" sx={{ mb: 0.75 }}>
                Results
              </Typography>
              <Box
                sx={{
                  border: "1px solid",
                  borderColor: "divider",
                  borderRadius: "8px",
                  minHeight: 120,
                  overflow: "auto",
                }}
              >
                {loading ? (
                  <Box
                    sx={{
                      minHeight: 120,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <CircularProgress size="sm" />
                  </Box>
                ) : selectableMembers.length === 0 ? (
                  <Box sx={{ p: 2 }}>
                    <Typography level="body-sm" color="neutral">
                      {query.trim().length > 0
                        ? "No matching workspace members."
                        : "No more workspace members available to add."}
                    </Typography>
                  </Box>
                ) : (
                  <List size="sm">
                    {selectableMembers.map((member) => (
                      <ListItem key={member.user_id}>
                        <ListItemButton
                          onClick={() => handleSelectUser(member)}
                          data-testid={`collaborator-option-${member.user_id}`}
                        >
                          <ListItemContent>
                            <Typography level="body-sm">
                              {member.email}
                            </Typography>
                          </ListItemContent>
                        </ListItemButton>
                      </ListItem>
                    ))}
                  </List>
                )}
              </Box>
            </Box>

            <Divider />

            <Box>
              <Typography level="body-sm" sx={{ mb: 0.75 }}>
                Current collaborators
              </Typography>
              {collaborators.length === 0 ? (
                <Typography level="body-sm" color="neutral">
                  No collaborators yet.
                </Typography>
              ) : (
                <List size="sm" sx={{ "--ListItem-paddingY": "0.5rem" }}>
                  {collaborators.map((collaborator) => (
                    <ListItem
                      key={collaborator.user_id}
                      endAction={
                        collaborator.role === "collaborator" ? (
                          <IconButton
                            size="sm"
                            color="danger"
                            variant="plain"
                            onClick={() =>
                              void handleRemoveCurrent(collaborator.user_id)
                            }
                            disabled={saving}
                            aria-label={`Remove ${collaborator.display_name}`}
                          >
                            <CloseIcon />
                          </IconButton>
                        ) : undefined
                      }
                    >
                      <ListItemContent>
                        <Stack
                          direction="row"
                          spacing={1}
                          alignItems="center"
                          flexWrap="wrap"
                          useFlexGap
                        >
                          <Typography level="body-sm">
                            {collaborator.display_name}
                          </Typography>
                          <Chip size="sm" variant="soft" color="neutral">
                            {collaborator.role}
                          </Chip>
                        </Stack>
                      </ListItemContent>
                    </ListItem>
                  ))}
                </List>
              )}
            </Box>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button
            variant="solid"
            onClick={handleAdd}
            disabled={selectedUsers.length === 0 || saving}
          >
            Add
          </Button>
          <Button variant="plain" color="neutral" onClick={onClose}>
            Cancel
          </Button>
        </DialogActions>
      </ModalDialog>
    </Modal>
  );
};

export default CollaboratorManagementModal;
